import React, { useEffect, useMemo, useRef, useState } from "react";
import Modal, { CommonModalProps } from "..";
import { Box, Button, CircularProgress, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useSelector } from "react-redux";
import {
  GetArtworkMeta,
  GetCulturesList,
  HandleChangeArtworkMetadata,
} from "../../../Redux/Reducers/redux-slice-bundle-maker";
import { assetService, cacheService } from "../../../Core/Api/services";
import { UpdateMetadataFieldsRequest } from "../../../Core/Api/Types/update-metadata-fields-request";
import { DbCultureInfos } from "../../../Core/Api/Types/db-culture-infos";
import { generateArtworkCultureIdsMetadata } from "../../../Redux/Utils/Bundlemaker/bundlemaker-utils";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { IDbBaseMetadataValueInfos } from "../../../Core/Api/Types/i-db-base-metadata-value-infos";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import ArtworksTable, { SelectedRows } from "../../../Pages/Bundlemaker/artworks-table";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

export type ModalBundlemakerAddArtworkToCultureProps = {
  closeModal: () => void;
  cultureId: number | null;
  onClose;
};

export type ModalAddArtworkEditProps = CommonModalProps<ModalBundlemakerAddArtworkToCultureProps>;

const ModalBundlemakerAddArtwork = React.forwardRef<any, ModalAddArtworkEditProps>((props, ref) => {
  const { t } = useTranslation();
  const { closeModal, cultureId, onClose } = props;
  const enqueueCustomSnackbar = useCustomSnackbar();
  const artworkTableRef = useRef<SelectedRows>(undefined);
  const dispatch = useDispatch();

  //------local state/select store values------

  const cultures = useSelector(GetCulturesList);
  const artworksMeta = useSelector(GetArtworkMeta);

  const [selectedCultures, setSelectedCultures] = useState<DbCultureInfos[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  // Init culture selection with receive cutlure
  useEffect(() => {
    if (cultureId) {
      const cultureToSet = cultures.find((x) => x.id === cultureId);
      cultureToSet && setSelectedCultures([cultureToSet]);
    }
  }, [cultureId]);

  const preSelectedArtworkIds = useMemo(() => {
    const result: number[] = [];
    artworksMeta.forEach((x) => {
      x.metadata
        .find((m) => cacheService.GetMetadata().find((y) => y.tagName === "artworkCultureIds")?.id === m.id)
        ?.value?.find((v) => v === selectedCultures[0]?.id) && result.push(x.id);
    });
    return result;
  }, [artworksMeta.length, selectedCultures[0]?.id]);

  const handleSave = () => {
    if (!(artworkTableRef && artworkTableRef.current)) return;
    setIsSaving(true);

    const selectedRows = artworkTableRef.current.getSelectedRows();
    const updatedArtworkIds: number[] = [];
    const metadata: IDbBaseMetadataValueInfos[] = [];
    const operations: number[] = [];

    // set operation variable as an artwork id array: negative id for artwork to remove positive id artwork for to add
    for (let i = 0; i < preSelectedArtworkIds.length; i++) {
      if (!selectedRows.find((x) => preSelectedArtworkIds[i] === x)) operations.push(-preSelectedArtworkIds[i]);
    }
    for (let i = 0; i < selectedRows.length; i++) {
      if (!preSelectedArtworkIds.find((x) => selectedRows[i] === x)) operations.push(selectedRows[i]);
    }

    // generate new cultureId metadata for each artwork id in operation array
    for (let i = 0; i < operations.length; i++) {
      const newMetaValues: number[] =
        operations[i] < 0
          ? artworksMeta
              .find((x) => x.id === Math.abs(operations[i]))
              .metadata.find(
                (x) => cacheService.GetMetadata().find((m) => m.tagName === "artworkCultureIds")?.id === x.id
              )
              ?.value?.filter((x) => x !== selectedCultures[0]?.id)
          : [
              ...(artworksMeta
                .find((x) => x.id === Math.abs(operations[i]))
                ?.metadata?.find(
                  (x) => cacheService.GetMetadata().find((m) => m.tagName === "artworkCultureIds")?.id === x.id
                )?.value || []),
              selectedCultures[0].id,
            ];

      const newMetadata = generateArtworkCultureIdsMetadata(newMetaValues);

      // perform call
      if (newMetadata) {
        updatedArtworkIds.push(operations[i]);
        metadata.push(newMetadata);
        assetService
          .UpdateMetadataFields(
            new UpdateMetadataFieldsRequest({
              objectId: Math.abs(operations[i]),
              metadata: [newMetadata],
            })
          )
          .then((res) => {
            if (res.success) {
              dispatch(
                HandleChangeArtworkMetadata({ artworkId: Math.abs(updatedArtworkIds[i]), metadata: [metadata[i]] })
              );
              if (i === operations.length - 1) {
                setIsSaving(false);
                closeModal();
                enqueueCustomSnackbar(t("alert:success.artworksUpdated"), "success");
              }
            } else {
              enqueueCustomSnackbar(res.message || t("alert:error.artworksUpdating"), "error");
              setIsSaving(false);
            }
          });
      }
    }
  };

  return (
    <Modal
      width={950}
      reference={ref}
      closeModal={closeModal}
      title={
        <Box sx={{ display: "flex" }}>
          <Tooltip title={t("modal:modalBundlermakerAddArtworkToCulture.title.cancelChanges")}>
            <IconButton
              sx={{ height: "20px", width: "20px", padding: "20px" }}
              onClick={() => artworkTableRef.current?.handleSetSelectedRows(preSelectedArtworkIds)}
            >
              <RestartAltIcon />
            </IconButton>
          </Tooltip>
          <Typography variant="subtitle2" sx={{ paddingTop: "12px" }}>
            {" "}
            {t("modal:modalBundlermakerAddArtworkToCulture.body.addImagesToCultureName", {
              cultureName: selectedCultures[0]?.englishName,
            })}
          </Typography>
        </Box>
      }
      footer={
        <Stack position="relative" direction="row" spacing={2}>
          <Button onClick={() => closeModal()} startIcon={<ArrowBackIcon />} color="inherit">
            {t("common:cancel")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleSave()}
            startIcon={<AddIcon />}
            sx={{ flex: 1 }}
            disabled={selectedCultures.length === 0 || isSaving}
          >
            {t("common:save")}
          </Button>
          {isSaving && (
            <CircularProgress
              size={24}
              sx={{
                position: "absolute",
                top: "8px",
                right: "12px",
              }}
            />
          )}
        </Stack>
      }
    >
      <Stack>
        <ArtworksTable
          preSelectedIds={preSelectedArtworkIds}
          select={true}
          ref={artworkTableRef || null || undefined}
          showActions={false}
        />
      </Stack>
    </Modal>
  );
});

export default ModalBundlemakerAddArtwork;
